import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import FirebaseContext from './context';
import { Firebase, LoadedSessionData } from './firebase';
import { usePublicConfig } from '../hooks/usePublicConfig';
import { Notification } from '../components/Notification';
import { interpolateAppText, useDefaultAppTexts } from '../hooks/useAppTexts';
import { ShopNotFound } from '../components/ShopNotFound';
import { fallbackLanguage, TReturnOptionsResponse } from '../../../../functions/src/shared';
// import { useLanguage } from '../hooks/useLanguage';

const firebase = new Firebase();

export const FirebaseContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [publicConfig, isLoadingPublicConfig, configError] = usePublicConfig(firebase);
  const appTexts = useDefaultAppTexts();
  const [loadedSessionData, setLoadedSessionData] = useState<LoadedSessionData>();
  const language = publicConfig?.language || fallbackLanguage;

  useEffect(() => {
    if (!publicConfig || loadedSessionData) {
      return;
    }

    const unsubscribe = firebase.auth.onAuthStateChanged(async (user) => {
      if (user === null) {
        return;
      }

      // if no data has been fetched yet, fetch the initial data
      try {
        setLoadedSessionData(await firebase.fetchInitialData(language));
      } catch (error) {
        // if there is an error fetching the initial data, sign out the user
        firebase.auth.signOut();
        console.error('Error on initial data fetch: ', error);
      }
    });

    return unsubscribe;
  }, [publicConfig, loadedSessionData, language]);

  const setReturnOptions = useCallback((returnOptions: TReturnOptionsResponse) => {
    setLoadedSessionData((prevState) => ({ ...prevState!, returnOptions }));
  }, []);

  const getShopName = useCallback(() => {
    return (firebase.shopName || loadedSessionData?.userData?.shop)?.split('.')[0];
  }, [loadedSessionData?.userData?.shop]);

  const getPathTo = useCallback(
    (destination: string) => {
      if (firebase.hasCustomDomain()) return `/${destination}`;

      return `/${getShopName()}/${destination}`;
    },
    [getShopName],
  );

  const resetSession = useCallback(async () => {
    await firebase.auth.signOut();
    setLoadedSessionData(undefined);
  }, []);

  const value = useMemo(
    () => ({
      ...firebase,
      getPathTo,
      getShopName,
      resetSession,
      publicConfig,
      loadedSessionData,
      setReturnOptions,
    }),
    [getPathTo, getShopName, loadedSessionData, publicConfig, resetSession, setReturnOptions],
  );

  if (!publicConfig && !isLoadingPublicConfig) {
    return <ShopNotFound />;
  }

  const loadingMarkup = <div className="custom-loader is-loading is-center-page"></div>;

  const errorMarkup = (
    <div className="items-list">
      <Notification
        status="warning"
        message={interpolateAppText(appTexts.generic.error.other, {
          supportEmail: publicConfig?.supportEmail,
        })}
      />
    </div>
  );

  return (
    <FirebaseContext.Provider value={value}>
      {configError ? errorMarkup : publicConfig ? children : loadingMarkup}
    </FirebaseContext.Provider>
  );
};
